import { useEffect } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import { endPoints, siteConfig } from "../../config/end-points";

// Note: import names are swapped intentionally — Login component is in ForgotPassword file
import Login from "./pages/ForgotPassword";
import ForgotPassword from "./pages/Login";
import "./auth.css";

const Auth = () => {
  const history = useHistory();
  const location = useLocation();

  const isLogin = location.pathname !== "/forgot_password";

  const passwordPanel = () => {
    history.push("/forgot_password");
  };

  const loginPanel = () => {
    history.push("/login");
  };

  useEffect(() => {
    document.title = isLogin
      ? `${endPoints.title} | Login`
      : `${endPoints.title} | Forgot Password`;
  }, [isLogin]);

  return (
    <div className="auth-redesigned">
      <div className="auth-page">
        {/* Left Panel — Brand / Marketing */}
        <div className="auth-left">
          <div className="auth-left-content">
            <h1>
              Create. Manage.<br />
              <span>Scale.</span>
            </h1>
            <p>
              The all-in-one platform for building
              AI&#8209;powered websites in minutes.
            </p>
            <div className="auth-illustration">
              {/* SVG illustration — website builder visual */}
              <svg viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "90%", height: "auto" }}>
                {/* Browser window */}
                <rect x="40" y="30" width="320" height="220" rx="14" fill="#FFFFFF" stroke="#E5E7EB" strokeWidth="1.5"/>
                {/* Title bar */}
                <rect x="40" y="30" width="320" height="36" rx="14" fill="#F9FAFB"/>
                <rect x="40" y="52" width="320" height="14" fill="#F9FAFB"/>
                {/* Window dots */}
                <circle cx="62" cy="48" r="5" fill="#EF4444" opacity="0.7"/>
                <circle cx="80" cy="48" r="5" fill="#F59E0B" opacity="0.7"/>
                <circle cx="98" cy="48" r="5" fill="#10B981" opacity="0.7"/>
                {/* URL bar */}
                <rect x="120" y="40" width="160" height="16" rx="8" fill="#E5E7EB"/>
                <rect x="128" y="45" width="80" height="6" rx="3" fill="#D1D5DB"/>

                {/* Page content - hero section */}
                <rect x="60" y="80" width="140" height="10" rx="5" fill="#7C3AED" opacity="0.8"/>
                <rect x="60" y="96" width="100" height="8" rx="4" fill="#C4B5FD"/>
                <rect x="60" y="112" width="120" height="6" rx="3" fill="#E5E7EB"/>
                <rect x="60" y="124" width="110" height="6" rx="3" fill="#E5E7EB"/>

                {/* CTA button */}
                <rect x="60" y="142" width="80" height="24" rx="8" fill="#7C3AED"/>
                <rect x="72" y="151" width="56" height="6" rx="3" fill="#FFFFFF"/>

                {/* Image placeholder */}
                <rect x="220" y="80" width="120" height="90" rx="10" fill="#F5F3FF"/>
                <circle cx="280" cy="115" r="20" fill="#EDE9FE"/>
                <path d="M265 145 L280 125 L295 145" fill="#DDD6FE"/>

                {/* Cards row */}
                <rect x="60" y="182" width="88" height="52" rx="8" fill="#F5F3FF" stroke="#EDE9FE" strokeWidth="1"/>
                <rect x="70" y="192" width="40" height="6" rx="3" fill="#7C3AED" opacity="0.6"/>
                <rect x="70" y="204" width="60" height="4" rx="2" fill="#D1D5DB"/>
                <rect x="70" y="214" width="50" height="4" rx="2" fill="#D1D5DB"/>

                <rect x="156" y="182" width="88" height="52" rx="8" fill="#F5F3FF" stroke="#EDE9FE" strokeWidth="1"/>
                <rect x="166" y="192" width="40" height="6" rx="3" fill="#8B5CF6" opacity="0.6"/>
                <rect x="166" y="204" width="60" height="4" rx="2" fill="#D1D5DB"/>
                <rect x="166" y="214" width="50" height="4" rx="2" fill="#D1D5DB"/>

                <rect x="252" y="182" width="88" height="52" rx="8" fill="#F5F3FF" stroke="#EDE9FE" strokeWidth="1"/>
                <rect x="262" y="192" width="40" height="6" rx="3" fill="#A78BFA" opacity="0.6"/>
                <rect x="262" y="204" width="60" height="4" rx="2" fill="#D1D5DB"/>
                <rect x="262" y="214" width="50" height="4" rx="2" fill="#D1D5DB"/>

                {/* Cursor */}
                <g transform="translate(185, 135)">
                  <path d="M0 0 L0 18 L5 14 L9 22 L12 20 L8 12 L14 12 Z" fill="#7C3AED" stroke="#FFFFFF" strokeWidth="1.5"/>
                </g>

                {/* Floating elements */}
                <rect x="10" y="100" width="24" height="24" rx="6" fill="#7C3AED" opacity="0.15" transform="rotate(-12 22 112)"/>
                <rect x="368" y="160" width="20" height="20" rx="6" fill="#8B5CF6" opacity="0.12" transform="rotate(15 378 170)"/>
                <circle cx="375" cy="60" r="10" fill="#EDE9FE" opacity="0.6"/>
                <circle cx="25" cy="200" r="8" fill="#DDD6FE" opacity="0.5"/>
              </svg>
            </div>
          </div>
        </div>

        {/* Right Panel — Form */}
        <div className="auth-right">
          <div className="auth-form-wrapper" key={isLogin ? "login" : "forgot"}>
            <div className="auth-form-logo">
              <img
                src={endPoints.logo}
                alt={endPoints.title}
              />
            </div>

            {isLogin ? (
              <>
                <h2>Sign in to your account</h2>
                <p className="auth-subtitle">Enter your credentials to access your dashboard.</p>
                {/* ForgotPassword variable = Login component (imports are swapped) */}
                <ForgotPassword onForgotClick={passwordPanel} />
              </>
            ) : (
              <>
                <h2>Forgot Password</h2>
                <p className="auth-subtitle">
                  Enter your email and we'll send you a link to reset your password.
                </p>
                {/* Login variable = ForgotPassword component (imports are swapped) */}
                <Login onBackToLogin={loginPanel} />
              </>
            )}

            {/* Footer */}
            <div className="auth-footer">
              <p>{siteConfig.copyright}</p>
              <Link to="/terms">Terms of Service</Link>
              <span className="auth-footer-divider">|</span>
              <Link to="/privacy">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
